import { isEqual, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/react-redux';
import { useSingleHiddenDocumentFields, useSingleHiddenFields } from '../../../../../hooks/useHiddenSingleFields';
import { OpenFieldSection } from '../../../../datasets/instances/store/types';
import { DocumentSpecificHiddenFields, HiddenFields, TableDatasetDefinition, TableDatasetFieldType } from '../../../../datasets/store';
import { Scrollable } from '../../../../shared/scrollable/Scrollable';
import { addOpenFieldSection, getReportOpenFieldSections, getSelectedDocumentNameIds, removeOpenFieldSection } from '../../store';
import styles from '../ReportDefinitions.module.scss';
import { TableCell } from './TableCell';
import { TableHeader } from './TableHeader';

interface TableDefinitionProps {
    tableDefinition: TableDatasetDefinition;
    hiddenFields: HiddenFields;
    datasetHidden?: boolean;
    documentSpecificHiddenFields: DocumentSpecificHiddenFields;
    parentHiddenDocumentNameIds?: number[];
    parentFieldId: string;
}

export const TableDefinition: React.FC<TableDefinitionProps> = ({ tableDefinition, hiddenFields, datasetHidden, documentSpecificHiddenFields, parentHiddenDocumentNameIds, parentFieldId }) => {
    const { datasetFields, datasetId } = tableDefinition;
    const dispatch = useAppDispatch();

    const openFieldsAndSections = useAppSelector(getReportOpenFieldSections);
    const reportDocumentNameIds = useAppSelector(getSelectedDocumentNameIds);

    const getSectionOpen = useCallback((fieldSection: OpenFieldSection) => openFieldsAndSections.some(openSection => isEqual(openSection, fieldSection)), [openFieldsAndSections]);

    const toggleSection = useCallback((fieldSection: OpenFieldSection) => {
        const sectionOpen = openFieldsAndSections.find(openSection => isEqual(openSection, fieldSection));
        sectionOpen ? dispatch(removeOpenFieldSection(sectionOpen)) : dispatch(addOpenFieldSection(fieldSection));
    }, [dispatch, openFieldsAndSections]);

    const currentHiddenDatasetFields = useMemo(() => hiddenFields[datasetId!] || [], [datasetId, hiddenFields]);

    const hiddenSingleFields = useSingleHiddenFields(currentHiddenDatasetFields);

    const columnIsHidden = useCallback((id: string) => datasetHidden || !!hiddenSingleFields.find(({ fieldId }) => fieldId === id), [hiddenSingleFields, datasetHidden]);

    const tableFirstCell = useCallback((fieldId: string) => datasetFields.filter(({ id }) => !hiddenSingleFields.map(({ fieldId }) => fieldId).includes(id!))[0].id === fieldId, [hiddenSingleFields, datasetFields]);

    const documentHiddenFields = useMemo(() => documentSpecificHiddenFields[datasetId!] || [], [datasetId, documentSpecificHiddenFields]);
    const documentHiddenSingleFields = useSingleHiddenDocumentFields(documentHiddenFields);
    const getColumnHiddenForAllDatasetDocuments = useCallback((hiddenFieldDocumentNameIds: number[]) => hiddenFieldDocumentNameIds.length > 0 && reportDocumentNameIds.every(documentNameId => hiddenFieldDocumentNameIds.includes(parseInt(documentNameId))), [reportDocumentNameIds]);
    const getColumnHiddenDocumentNames = useCallback((id: string) => {
        const hiddenFieldDocumentNameIds = documentHiddenSingleFields.find(({ fieldId }) => fieldId === id)?.documentNameIds;
        if (!isUndefined(hiddenFieldDocumentNameIds)) {
            const allHiddenDocumentNameIds = [...hiddenFieldDocumentNameIds, ...parentHiddenDocumentNameIds || []];
            return { hiddenFieldDocumentNameIds: allHiddenDocumentNameIds };
        }
        return { hiddenDocumentNames: [], hiddenFieldDocumentNameIds: [] };
    }, [documentHiddenSingleFields, parentHiddenDocumentNameIds]);

    return (
        <div className={styles.tableDefinitionWrapper} data-testid='table-dataset-definition-wrapper' style={{ height: '100%', maxHeight: '400px' }}>
            <Scrollable>
                <div className={styles.tableHeader}>
                    {datasetFields.map(field => {
                        const { label, description, refLabel, settings, id, type } = field;
                        const columnHidden = columnIsHidden(id!);
                        const { hiddenFieldDocumentNameIds } = getColumnHiddenDocumentNames(id!);
                        const columnHiddenForAllDatasetDocuments = getColumnHiddenForAllDatasetDocuments(hiddenFieldDocumentNameIds);

                        if (columnHidden || columnHiddenForAllDatasetDocuments) {
                            return null;
                        }
                        const isFirstCell = tableFirstCell(id!);
                        return (
                            <TableHeader
                                key={id!}
                                label={label}
                                description={description}
                                refLabel={refLabel}
                                showRef={settings.showRef}
                                id={id!}
                                type={type as TableDatasetFieldType}
                                isFirstCell={isFirstCell}
                                datasetId={tableDefinition.datasetId!}
                                parentFieldId={parentFieldId}
                                settings={settings}
                            />
                        );
                    })}
                </div>
                <div className={styles.tableBody} style={{ height: 'calc(100% - 50px)', maxHeight: '350px' }}>
                    <div className={styles.tableRow}>
                        {datasetFields.map((field, index) => {
                            const columnHidden = columnIsHidden(field.id!);
                            const { hiddenFieldDocumentNameIds } = getColumnHiddenDocumentNames(field.id!);
                            const columnHiddenForAllDatasetDocuments = getColumnHiddenForAllDatasetDocuments(hiddenFieldDocumentNameIds);

                            if (columnHidden || columnHiddenForAllDatasetDocuments) {
                                return null;
                            }
                            const isFirstCell = tableFirstCell(field.id!);
                            return (
                                <TableCell
                                    key={field.id!}
                                    field={field}
                                    index={index}
                                    isFirstCell={isFirstCell}
                                    datasetId={datasetId!}
                                    toggleSection={toggleSection}
                                    getSectionOpen={getSectionOpen}
                                />
                            );
                        })}
                    </div>
                </div>
            </Scrollable>
        </div>
    );
};
